/**
 * Shared class tokens for the mobile / PWA shell.
 * RBCFS blue primary + Zuper-like pastel accents (not an orange rebrand).
 */

export const mobile = {
  page: 'native-page space-y-3 lg:space-y-6 px-3 sm:px-4 min-w-0 pb-2',
  pageTight: 'native-page space-y-2 lg:space-y-4 px-3 sm:px-4 min-w-0 pb-2',

  /** Sticky in-page title bar (below Layout chrome) */
  stickyHeader:
    'sticky top-0 z-20 -mx-3 sm:-mx-4 px-3 sm:px-4 py-2.5 bg-white/95 backdrop-blur-md border-b border-gray-200/80',

  title: 'text-[1.375rem] lg:text-2xl font-bold text-gray-900 tracking-tight',
  subtitle: 'text-sm text-gray-500 mt-0.5',

  /** Grouped list container (iOS-style inset group) */
  listGroup:
    'bg-white rounded-2xl border border-gray-200/90 shadow-sm overflow-hidden divide-y divide-gray-100',

  /** Standalone interactive card / row surface */
  surface: 'bg-white rounded-2xl border border-gray-200/90 shadow-sm',

  row: 'w-full text-left px-4 py-3.5 min-h-[52px] active:bg-gray-50 transition-colors',
  rowPressed: 'active:bg-gray-50',

  sectionLabel:
    'px-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400',

  primaryBtn:
    'inline-flex items-center justify-center gap-2 min-h-[48px] px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50',
  primaryBtnPill:
    'inline-flex items-center justify-center gap-2 min-h-[44px] px-5 py-2.5 bg-blue-600 text-white rounded-full text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 shadow-sm shadow-blue-600/25',
  secondaryBtn:
    'inline-flex items-center justify-center gap-2 min-h-[48px] px-4 py-2.5 bg-white text-gray-800 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50',
  dangerBtn:
    'inline-flex items-center justify-center gap-2 min-h-[48px] px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 active:bg-red-800 transition-colors disabled:opacity-50',
  successBtn:
    'inline-flex items-center justify-center gap-2 min-h-[48px] px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 active:bg-green-800 transition-colors disabled:opacity-50',
  ghostBtn:
    'inline-flex items-center justify-center gap-2 min-h-[44px] min-w-[44px] px-2 rounded-xl text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors',

  field:
    'w-full min-h-[48px] px-3.5 py-2.5 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100',
  label: 'block text-sm font-medium text-gray-700 mb-1.5',

  badge:
    'inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full',

  emptyWrap: 'text-center py-14 px-4',
  emptyIcon: 'mx-auto h-12 w-12 text-gray-300',
  emptyTitle: 'mt-3 text-sm font-semibold text-gray-900',
  emptyBody: 'mt-1 text-sm text-gray-500 max-w-xs mx-auto',

  /** Pastel fill tiles (status summary, accents) */
  pastel: {
    rose: 'bg-rose-50 border-rose-100 text-rose-800',
    amber: 'bg-amber-50 border-amber-100 text-amber-800',
    sky: 'bg-sky-50 border-sky-100 text-sky-800',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-800',
    blue: 'bg-blue-50 border-blue-100 text-blue-800',
    teal: 'bg-teal-50 border-teal-100 text-teal-800',
    violet: 'bg-violet-50 border-violet-100 text-violet-800',
    slate: 'bg-slate-100 border-slate-200 text-slate-700',
  } as const,

  iconTile: {
    rose: 'bg-rose-100 text-rose-600',
    amber: 'bg-amber-100 text-amber-600',
    sky: 'bg-sky-100 text-sky-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
    teal: 'bg-teal-100 text-teal-600',
    violet: 'bg-violet-100 text-violet-600',
    slate: 'bg-slate-200 text-slate-600',
  } as const,

  /** Circular quick-action button (job detail) */
  quickAction:
    'inline-flex flex-col items-center gap-1.5 min-w-0 flex-1 disabled:opacity-45',
  quickActionCircle:
    'inline-flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-blue-600 active:bg-sky-100 transition-colors',
  quickActionCircleMuted:
    'inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400',
  quickActionLabel: 'text-[11px] font-medium text-gray-700 text-center leading-tight',

  tabActive: 'border-b-2 border-blue-600 text-blue-700 font-semibold',
  tabIdle: 'border-b-2 border-transparent text-gray-500',

  stickyCta:
    'fixed inset-x-0 z-30 border-t border-gray-200 bg-white px-3 py-2.5 shadow-[0_-4px_16px_rgba(15,23,42,0.08)]',
  stickyCtaBtn:
    'w-full min-h-[52px] px-4 py-3 text-[15px] font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 bg-blue-600 text-white active:bg-blue-800 shadow-md shadow-blue-600/20',
  stickyCtaBtnSuccess:
    'w-full min-h-[52px] px-4 py-3 text-[15px] font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 bg-emerald-600 text-white active:bg-emerald-700 shadow-md shadow-emerald-600/20',

  /** Hide desktop tables / show mobile lists */
  mobileOnly: 'lg:hidden',
  desktopOnly: 'hidden lg:block',
  desktopGrid: 'hidden lg:grid',
} as const

export type MobilePastelTone = keyof typeof mobile.pastel
